import { type CamelCased } from '../utility';

export enum ScreenShareResolutions {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High'
}

export enum InstructorTARestrictedAccess {
    Everyone = 'everyone',
    InstructorsTAsOnly = 'instructor_ta',
    NoOne = 'no_one'
}

export enum ParticipationSortType {
    Engagement = 'engagement',
    TalkTime = 'talkTime'
}

export enum UnauthUserAccess {
    Allow = 'allow',
    Deny = 'deny',
    SignIn = 'signin'
}

export enum ClassJoinPermission {
    RosterOnly = 'roster only',
    AuthenticatedUsers = 'authenticated users',
    All = 'all'
}

type SchoolSettingsOptions = {
    allowCustomVirtualBackgroundUpload: boolean;
    allowInstructorsTranscriptDownload: boolean;
    allowLmsGroupBreakouts: boolean;
    allowNextGenMobile: boolean;
    allowTabLocking: boolean;
    allowVanillaZoom: boolean;
    allowVirtualBackgroundInModeratePerfMode: boolean;
    attendanceGradeTardyScore: number;
    classJoinPermission: ClassJoinPermission;
    disableCameraMicrophoneWhenRecording: boolean;
    disableClassBackgrounds: boolean;
    disableTeamsRecording: boolean;
    enableACSOptimalVideoCount: boolean;
    enableLearnerEngagement: boolean;
    enableNextGenFilesGoogleServiceAccount: boolean;
    enablePrivateDiscussion: boolean;
    excludedEmbedDomains: string[];
    forceWebAsChromebook: boolean;
    hasAiTa: boolean;
    hasAllBreakoutTranscripts: boolean;
    hasBreakoutRoomCrossCommunication: boolean;
    hasClassAuth: boolean;
    hasEndUserPerformanceTest: boolean;
    hasEndUserSurvey: InstructorTARestrictedAccess;
    hasFileAnnotations: boolean;
    hasInMeetingTechCheck: boolean;
    hasMoodle: boolean;
    hasMsftAuth: boolean;
    hasNextGenChat: boolean;
    hasNextGenChatActivityFeed: boolean;
    hasNextGenFiles: boolean;
    hasNextGenGoogleDrive: boolean;
    hasNextGenLearningToolSidebar: boolean;
    hasNextGenOneDrive: boolean;
    hasNextGenPolls: boolean;
    hasNextGenSidebar: boolean;
    hasPresentations: boolean;
    hasSSOFeature: boolean;
    hasScheduleLinkOnClassLanding: boolean;
    hasSyncSeatingCharts: boolean;
    hasTabScreenshare: boolean;
    hasTabScreenshareAnnotation: boolean;
    hasTabScreenshareShareAudio: boolean;
    hasTelephony: boolean;
    hasWhiteboardBORType: boolean;
    orgScreenshareRes: ScreenShareResolutions;
    participationSort: ParticipationSortType;
    permittedEmbedDomains: string[];
    recordingsDisableDownloads: boolean;
    recordingsDisablePublicOption: boolean;
    recordingsRestrictChatParticipants: boolean;
    rosterSelfEnrollment: boolean;
    salesTrainingMmode: boolean;
    subClasses: boolean;
    teamsRecordingPauseResume: boolean;
    unauthUserAccess: UnauthUserAccess;
    unauthUserText: string | null;
    updateMicrosoftLicenseDays: number;
    useIdentifierInRoster: boolean;
    userAccountCreationDomains: string[];
};

export type SchoolSettingsRaw = {
    school_uuid: string;
    prefix: string;
    code_name: string;
    name: string;
    logo: string;
    language: null;
    settings_id: string | null;
    zoom_app_id: string;
    is_collab: boolean;
    meeting_provider_acs_allowed: boolean;
    account_id: null;
    administrator_group_id: string;
    allow_instructor_swap: boolean;
    allow_pmi: boolean;
    assistants_title: string;
    attendance_participants_can_view: boolean;
    attendance_tab_access: string;
    auto_transcriptions: boolean;
    auto_verify: 'never' | 'always' | 'optional';
    class_access: string;
    class_notes: boolean;
    contact_email: string;
    contact_name: string;
    created_at: string;
    document_title: 'syllabus' | 'rubric' | 'outline' | 'agenda' | 'schedule' | 'overview' | 'curriculum' | 'program';
    domains: string;
    enable_breakout_room_cloud_recording: boolean;
    enable_log_auto_upload: boolean;
    enforce_class_minimum_version: boolean;
    force_web_app_chromebooks: boolean;
    forced_authentication: boolean;
    gradebook_title: 'gradebook' | 'reports' | 'results' | 'outcomes' | 'feedback' | 'completions' | 'credits';
    has_ai_ta: boolean;
    has_app: boolean;
    has_assessments: boolean;
    has_assignments: boolean;
    has_chat: boolean;
    has_document: boolean;
    has_forced_auth: boolean;
    has_google: boolean;
    has_gradebook: boolean;
    has_lms: boolean;
    has_lose_focus_tracking: boolean;
    has_ms_office: boolean;
    has_panic_mode: boolean;
    has_polling: boolean;
    has_proctoring: boolean;
    has_share_files: boolean;
    has_surveys: boolean;
    has_web_app: boolean;
    has_whiteboard: boolean;
    has_zoom: boolean;
    host_title: string;
    id: number;
    join_class_by_meeting_id: boolean;
    notify_outdated_class_version: boolean;
    organization_type: 'academic' | 'corporate';
    prevent_leave_session_redirect: boolean;
    proctoring_title: string;
    prompt_students_for_feedback: boolean;
    prompt_teachers_for_feedback: boolean;
    recording_auto_save: boolean;
    recording_cloud_save: boolean;
    recording_interactive_player: boolean;
    recording_local_save: boolean;
    recording_participants_can_view: boolean;
    recording_tab_access: string;
    restrict_sessions_recordings_by_groups: boolean;
    roster_title: 'roster' | 'roll' | 'participants';
    send_invite_emails: boolean;
    show_meeting_info: boolean;
    students_title: string;
    teacher_group_id: null;
    tech_support_group_id: null;
    updated_at: string;
    vanity_url: string;
    verification_code: string;
    instructor_chat_download: boolean;
    attendance: boolean;
    persistent_chat: boolean;
    notes: boolean;
    lms_api: boolean;
    lms_api_beta: boolean;
    whiteboard_screenshot_feature: boolean;
    web_app_upgrade: boolean;
    options: SchoolSettingsOptions;
};

export type SchoolSettings = CamelCased<SchoolSettingsRaw>;
